import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import dayjs from '../util/date.js';
import Utils from '../util/Utils.jsx';

import AddProjectModalXer from './AddProjectModalXer.jsx';
import AddProjectModalKml from './AddProjectModalKml.jsx';
import AddProjectModalMapSettings from './AddProjectModalMapSettings.jsx';
import AddProjectModalProjectSettings from './AddProjectModalProjectSettings.jsx';
import { getDatasets } from '../offline-features/getDatasets.js';
import { useStateValue } from '../context/state.jsx';
import { openAlert } from '../context/alert.js';
import { setDatasets, setDatasetInfo, setShowAddProjectModal } from '../context/datasets.js';
import { closeDataset } from '../context/openDatasets.js';
import { focusProject } from '../util/openProject.js';
import { localIconHrefsFromKml } from '../util/kmlMap.js';
import { useNavigate } from 'react-router-dom';

const AddProjectModal = ({ shown }) => {
	const navigate = useNavigate();
	const steps = [
		'Schedule',
		'KML',
		'Map Settings',
		'Project Settings'
	];

	const initialState = {
		datasetId: null,
		xerFile: null,
		kmlFile: null,
		overlayFile: null,
		startDate: '',
		endDate: '',
		remainingDurationUnit: 'hours',
		allowNotStartDurationChanges: false,
		hoursPerDay: '10',
		displayTime: false,
		step: 0,
		activityCodeTypes: null,
		kmlIconWarning: null,
		xerProjects: null,
		xerProjId: '',
		xerFilename: '',
		importNote: ''
	};

	const [{
		datasetId,
		xerFile,
		kmlFile,
		overlayFile,
		startDate,
		endDate,
		remainingDurationUnit,
		allowNotStartDurationChanges,
		hoursPerDay,
		displayTime,
		step,
		activityCodeTypes,
		kmlIconWarning,
		xerProjects,
		xerProjId,
		xerFilename,
		importNote
	}, setState] = useState({ ...initialState });

	const [{ currentDataset, datasets }, dispatch] = useStateValue();

	const resetState = () => {
		setState({ ...initialState });
	};

	const addProjectXerFileChange = e => {
		const file = e.target.files[0];
		if (file && /\.mpp$/i.test(file.name)) {
			e.target.value = '';
			dispatch(openAlert(
				'Save as XML first',
				'Binary MS Project files (.mpp) are not supported. In MS Project choose File → Save As and pick XML (*.xml), then upload that file.'
			));
			return;
		}
		setState(prevState => ({ ...prevState, xerFile: file || null, xerProjects: null, xerProjId: '' }));
	};

	const addProjectKmlFileChange = e => {
		const file = e.target.files && e.target.files[0];
		setState(prevState => ({ ...prevState, kmlFile: file || null, kmlIconWarning: null }));
		if (!file || typeof file.text !== 'function') {
			return;
		}
		file.text().then(text => {
			const hrefs = localIconHrefsFromKml(text);
			setState(prevState => {
				if (prevState.kmlFile !== file) {
					return prevState;
				}
				return { ...prevState, kmlIconWarning: hrefs };
			});
		}).catch(() => {});
	};

	const addProjectOverlayFileChange = e => {
		setState(prevState => ({ ...prevState, overlayFile: e.target.files[0] }));
	};

	const addProjectStartDateChange = (dates, string, flatpickr) => {
		flatpickr.close(); // Necessary for some reason for mobile browsers

		const startDateNew = dates.length ? dates[0].valueOf() : 0;
		setState(prevState => ({ ...prevState, startDate: startDateNew }));
		const postData = {
			dataset: datasetId,
			startDate: startDateNew ? dayjs(startDateNew).format('YYYY-MM-DD') : ''
		};
		Utils.ajax('updateStartDate', postData, dispatch);
	};

	const addProjectEndDateChange = (dates, string, flatpickr) => {
		flatpickr.close(); // Necessary for some reason for mobile browsers

		const endDateNew = dates.length ? dates[0].valueOf() : 0;
		setState(prevState => ({ ...prevState, endDate: endDateNew }));
		const postData = {
			dataset: datasetId,
			endDate: endDateNew ? dayjs(endDateNew).format('YYYY-MM-DD') : ''
		};
		Utils.ajax('updateEndDate', postData, dispatch);
	};

	const addProjectRemainingDurationUnitChange = e => {
		const remainingDurationUnitNew = e.target.value;
		setState(prevState => ({ ...prevState, remainingDurationUnit: remainingDurationUnitNew }));
		const postData = {
			dataset: datasetId,
			remainingDurationUnit: remainingDurationUnitNew
		};
		Utils.ajax('updateRemainingDurationUnit', postData, dispatch);
	};

	const addProjectAllowNotStartDurationChanges = e => {
		setState(prevState => ({ ...prevState, allowNotStartDurationChanges: e.target.checked }));
		const postData = {
			dataset: datasetId,
			allow: e.target.checked
		};
		Utils.ajax('updateAllowNotStartDurationChanges', postData, dispatch);
	};

	const addProjectHoursPerDayChange = e => {
		const hoursPerDayNew = e.target.value;
		if (isNaN(hoursPerDayNew) || hoursPerDayNew < 1) {
			dispatch(openAlert('Hours per day', 'The hours per day field must be numeric and greater than 0.'));
			return;
		}
		setState(prevState => ({ ...prevState, hoursPerDay: hoursPerDayNew }));
		const postData = {
			datasetId,
			hoursPerDay: hoursPerDayNew
		};
		Utils.ajax('updateHoursPerDay', postData, dispatch);
	};

	const addProjectUpdateDisplayTime = e => {
		const displayTimeNew = e.target.checked;
		setState(prevState => ({ ...prevState, displayTime: displayTimeNew }));
		const postData = {
			dataset: datasetId,
			displayTime: displayTimeNew
		};
		Utils.ajax('updateDisplayTime', postData, dispatch);
	};

	const updateAreaProperty = areaProperty => {
		const postData = {
			datasetId,
			areaProperty
		};
		Utils.ajax('updateAreaProperty', postData, dispatch);
	};

	const deleteProject = datasetId => {
		Utils.ajax('deleteDataset', { dataset: datasetId }, dispatch, result => {
			if (result.success) {
				if (currentDataset === datasetId) {
					dispatch(setDatasetInfo(null));
				}
				dispatch(closeDataset(datasetId));
				getDatasets(dispatch).then(result => {
					if (result.success) {
						dispatch(setDatasets(result.datasets));
					}
				});
			}
		});
	};

	const rememberImport = result => {
		const note = result && result.importWarning && result.importReport && result.importReport.externalLinkCount > 0
			? result.importWarning
			: '';
		if (note) {
			dispatch(openAlert('External links', note));
		}
		setState(prevState => ({
			...prevState,
			datasetId: result.datasetId,
			activityCodeTypes: result.activityCodeTypes,
			xerProjects: null,
			xerFilename: result.xerFilename || prevState.xerFilename,
			importNote: note
		}));
		nextStep();
	};

	const xerStepNext = () => {
		if (datasetId !== null && !(xerProjects && xerProjects.length)) {
			nextStep();
			return;
		}
		if (!xerFile && !(xerProjects && xerProjects.length)) {
			dispatch(openAlert('Schedule file required', 'Choose a P6 XER or MS Project XML file before continuing.'));
			return;
		}
		if (xerProjects && xerProjects.length) {
			if (!xerProjId) {
				dispatch(openAlert('Choose a P6 project', 'Select the project to import.'));
				return;
			}
			const formData = new FormData();
			formData.append('dataset', datasetId);
			formData.append('projId', xerProjId);
			formData.append('xerFilename', xerFilename);
			Utils.ajax('importXerProject', formData, dispatch, result => {
				if (!result || !result.success) {
					dispatch(openAlert('Add project failed', (result && result.error) || 'Unable to import that P6 project.'));
					return;
				}
				rememberImport(result);
			});
			return;
		}
		const formData = new FormData();
		formData.append('xer', xerFile);
		formData.append('projectDataType', 'uploadXer');
		Utils.ajax('createDatasetFromXER', formData, dispatch, result => {
			if (!result || !result.success) {
				dispatch(openAlert('Add project failed', (result && result.error) || 'Unable to create the project from this schedule file.'));
				return;
			}
			if (result.needsProjectChoice) {
				const projects = result.projects || [];
				setState(prevState => ({
					...prevState,
					datasetId: result.datasetId,
					xerFilename: result.xerFilename || '',
					xerProjects: projects,
					xerProjId: result.preselect ? String(result.preselect) : (projects[0] ? String(projects[0].projId) : '')
				}));
				return;
			}
			rememberImport(result);
		});
	};

	const kmlStepNext = () => {
		if (datasetId === null) {
			return;
		}

		if (kmlFile === null) {
			setState(prevState => ({ ...prevState, step: step + 2 }));
			return;
		}

		const formData = new FormData();
		formData.append('kml', kmlFile);
		formData.append('dataset', datasetId);

		Utils.ajax('setNewKmlFile', formData, dispatch, result => {
			nextStep();
		});
	};

	const mapsStepNext = () => {
		if (datasetId === null) {
			console.log('error');
			return;
		}

		// Overlay is optional — skip the upload round-trip when none was chosen.
		if (overlayFile === null) {
			nextStep();
			return;
		}

		const formData = new FormData();
		formData.append('overlay', overlayFile);
		formData.append('dataset', datasetId);

		Utils.ajax('setNewOverlayFile', formData, dispatch, result => {
			nextStep();
		});
	};

	const nextStep = () => {
		if (step === steps.length - 1) {
			return;
		}
		setState(prevState => ({ ...prevState, step: step + 1 }));
	};

	/*
	const previousStep = () => {
		if (step === 0) {
			return;
		}
		setState(prevState => ({ ...prevState, step: step - 1 }));
	};
	*/

	const confirmAndClose = () => {
		if (datasetId) {
			dispatch(openAlert('Cancel?', 'Your project is not finished being set up. Are you sure you want to cancel?', [{
				text: 'No, continue project setup',
				action: () => true
			}, {
				bsStyle: 'danger',
				text: 'Yes, cancel',
				action: () => {
					console.log('cancel');
					deleteProject(datasetId);
					resetState();
					dispatch(setShowAddProjectModal(false));
				}
			}]));
		} else {
			dispatch(setShowAddProjectModal(false));
		}
	};

	const finish = () => {
		const newDatasetId = datasetId;
		resetState();
		dispatch(setShowAddProjectModal(false));
		if (!newDatasetId) {
			return;
		}

		const openProject = () => {
			focusProject({
				dispatch,
				navigate,
				datasetId: newDatasetId
			}).catch(() => {});
		};

		getDatasets(dispatch).then(result => {
			if (result.success) {
				dispatch(setDatasets(result.datasets));
			}
			openProject();
		}).catch(() => {
			openProject();
		});
	};

	const modalContentItems = [
		<AddProjectModalXer
			key="xer"
			steps={steps}
			step={step}
			hasFile={xerFile !== null}
			confirmAndClose={confirmAndClose}
			next={xerStepNext}
			addProjectXerFileChange={addProjectXerFileChange}
			xerProjects={xerProjects}
			xerProjId={xerProjId}
			onProjChange={e => setState(prevState => ({ ...prevState, xerProjId: e.target.value }))}
		/>,
		<AddProjectModalKml
			key="kml"
			steps={steps}
			step={step}
			confirmAndClose={confirmAndClose}
			next={kmlStepNext}
			addProjectKmlFileChange={addProjectKmlFileChange}
			kmlIconWarning={kmlIconWarning}
		/>,
		<AddProjectModalMapSettings
			key="maps"
			steps={steps}
			step={step}
			confirmAndClose={confirmAndClose}
			next={mapsStepNext}
			addProjectOverlayFileChange={addProjectOverlayFileChange}
			activityCodeTypes={activityCodeTypes}
			updateAreaProperty={updateAreaProperty}
			kmlIconWarning={kmlIconWarning}
		/>,
		<AddProjectModalProjectSettings
			key="extra"
			steps={steps}
			step={step}
			confirmAndClose={confirmAndClose}
			finish={finish}
			startDate={startDate}
			endDate={endDate}
			remainingDurationUnit={remainingDurationUnit}
			hoursPerDay={hoursPerDay}
			displayTime={displayTime}
			allowNotStartDurationChanges={allowNotStartDurationChanges}
			addProjectStartDateChange={addProjectStartDateChange}
			addProjectEndDateChange={addProjectEndDateChange}
			addProjectRemainingDurationUnitChange={addProjectRemainingDurationUnitChange}
			addProjectHoursPerDayChange={addProjectHoursPerDayChange}
			addProjectUpdateDisplayTime={addProjectUpdateDisplayTime}
			addProjectAllowNotStartDurationChanges={addProjectAllowNotStartDurationChanges}
		/>
	];
	const modalContentItem = modalContentItems[step];

	return (
		<Modal
			show={shown}
			onHide={confirmAndClose}
			container={this}>
			<Modal.Header closeButton>
				<Modal.Title>Add Project</Modal.Title>
			</Modal.Header>
			{importNote
				? <div className="alert alert-info" role="status" style={{ margin: '12px 15px 0' }}>{importNote}</div>
				: null}
			{modalContentItem}
		</Modal>
	);
};

AddProjectModal.propTypes = {
	shown: PropTypes.bool
};

export default AddProjectModal;